import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { navigatorLock } from '@supabase/auth-js';

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLiveDomain = hostname === 'cartlens.co' || hostname === 'www.cartlens.co';

const supabaseUrl = (isLiveDomain 
  ? import.meta.env.VITE_SUPABASE_URL 
  : (import.meta.env.VITE_PREVIEW_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL)).trim();

const supabaseKey = (isLiveDomain 
  ? import.meta.env.VITE_SUPABASE_ANON_KEY 
  : (import.meta.env.VITE_PREVIEW_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY)).trim();

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase URL and Key are required!');
}

let singletonClient: SupabaseClient | null = null;

// Who the client must authenticate as. While a user is signed in, no request
// may go out without their Clerk token: supabase-js would fall back to the anon
// key, and RLS then filters every write to zero rows while PostgREST still
// answers 204, so saves "succeed" without changing anything.
//
// Clerk can drop its session in the browser while it is still valid on the
// server: when the token poll gets a 4xx (e.g. 422 missing_expired_token after
// the tab slept), Clerk refetches the client, and if that fetch answers 500 it
// signs out locally (handleUnauthenticated -> setActive({ session: null })).
// getToken() then resolves null until the session is set active again.
let expectedAuth: { userId: string; sessionId: string | null } | null = null;
let sessionLost = false;
const sessionLostListeners = new Set<() => void>();

const setSessionLost = (lost: boolean) => {
  if (sessionLost === lost) return;
  sessionLost = lost;
  sessionLostListeners.forEach(listener => listener());
};

export class SupabaseSessionLostError extends Error {
  constructor() {
    super('Your session could not be refreshed, so nothing was sent. Restore the session and try again.');
    this.name = 'SupabaseSessionLostError';
  }
}

/** Called by App with the signed-in Clerk user and session. */
export const setSupabaseAuthUser = (userId: string, sessionId: string | null) => {
  expectedAuth = { userId, sessionId };
};

/** The user signed out on purpose (or the session ended server-side): anon requests are fine again. */
export const clearSupabaseAuthUser = () => {
  expectedAuth = null;
  setSessionLost(false);
};

export const getExpectedSupabaseAuth = () => expectedAuth;
export const markSupabaseSessionLost = () => setSessionLost(true);
export const markSupabaseSessionRestored = () => setSessionLost(false);
export const isSupabaseSessionLost = () => sessionLost;
export const subscribeSupabaseSessionLost = (listener: () => void) => {
  sessionLostListeners.add(listener);
  return () => { sessionLostListeners.delete(listener); };
};

/**
 * Provides a singleton Supabase client instance.
 * @param getAccessToken Optional function to retrieve a session token (e.g. from Clerk)
 * @returns {SupabaseClient}
 */
export const getSupabaseClient = (getAccessToken?: () => Promise<string | null>): SupabaseClient => {
  if (singletonClient) return singletonClient;

  singletonClient = createClient(supabaseUrl, supabaseKey, {
    auth: {
      // `lockAcquireTimeout` is silently dropped by supabase-js's own auth
      // options (it isn't in the allowlist _initSupabaseAuthClient forwards
      // to GoTrueClient), so GoTrueClient falls back to its hardcoded 10s
      // default — that's the exact "timed out waiting 10000ms" seen in
      // production. Overriding `lock` directly is the only way to actually
      // raise the timeout.
      lock: (name, _acquireTimeout, fn) => navigatorLock(name, 60000, fn),
    },
    // Realtime otherwise asks supabase.auth for a session (there is none: Clerk
    // signs us in), joins as anon, and RLS hides every row. It calls this on
    // connect and on each heartbeat, which keeps the short-lived Clerk token fresh.
    realtime: {
      accessToken: async () => (getAccessToken ? await getAccessToken().catch(() => null) : null) ?? supabaseKey,
    },
    global: {
      fetch: async (input, init) => {
        const token = getAccessToken ? await getAccessToken() : null;
        if (!token && expectedAuth) {
          markSupabaseSessionLost();
          throw new SupabaseSessionLostError();
        }
        if (token) markSupabaseSessionRestored();
        const headers = new Headers(init?.headers);
        
        // Ensure the apikey is always present
        headers.set('apikey', supabaseKey);
        
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        
        return fetch(input, { ...init, headers });
      },
    },
  });

  return singletonClient;
};

// Keep legacy for backward compatibility during transition
export const getNewSupabaseClient = getSupabaseClient;